import "./Patient.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useState } from "react";
import loginService from "../../services/login-service";
import userService from "../../services/user-service";
import foodstuffService from "../../services/foodstuff-service";
import { GlobalContext } from "../GlobalContext";
import XSquareIcon from "../../assets/imgs/x-square-icon.svg";
import ChevronRightIcon from "../../assets/imgs/chevron-right-icon.svg";

import CaloriesIcon from "../../assets/imgs/calories-icon.svg";
import ProteinIcon from "../../assets/imgs/protein-icon.svg";
import CarbohydratesIcon from "../../assets/imgs/carbo-icon.svg";

import DefaultUserIcon from "../../assets/imgs/default-user-icon.png";
import MealPlanIcon from "../../assets/imgs/meal-plan-icon.svg";
import PinkDishIcon from "../../assets/imgs/pink-dish-icon.svg";

import DefaultButton from "../../components/DefaultButton/DefaultButton";
import DefaultDropdown from "../../components/DefaultDropdown/DefaultDropdown";
import TextInput from "../../components/TextInput/TextInput";

import { useParams } from "react-router-dom";
import DefaultModal from "../../components/DefaultModal/DefaultModal";
import mealPlanService from "../../services/meal-plan-service";
import NotificationBar from "../../components/NotificationBar/NotificationBar";

const mealOptions = [
  {
    value: "BREAKFAST",
    label: "Café da manhã",
  },
  {
    value: "MORNING_SNACK",
    label: "Lanche da manhã",
  },
  {
    value: "LUNCH",
    label: "Almoço",
  },
  {
    value: "AFTERNOON_SNACK",
    label: "Lanche da tarde",
  },
  {
    value: "DINNER",
    label: "Jantar",
  },
  {
    value: "SUPPER",
    label: "Ceia",
  },
]

const Patient = () => {
  const [currentUser, setCurrentUser] = useState();
  const [patientId, setPatientId] = useState(useParams().id);
  const [currentSection, setCurrentSection] = useState("SHOW_MEAL_PLAN");
  const [searchedFoodstuffs, setSearchedFoodstuffs] = useState([]);
  const [selectedFoodstuff, setSelectedFoodstuff] = useState();
  const [selectedMealPeriod, setSelectedMealPeriod] = useState();
  const [showAddFoodstuffModal, setShowAddFoodstuffModal] = useState(false);
  const [showDisableMealPlanModal, setShowDisableMealPlanModal] = useState(false);
  const [tempMealPlan, setTempMealPlan] = useState();
  const [mealPlan, setMealPlan] = useState();
  const [patient, setPatient] = useState();
  const [notificationBar, setNotificationBar] = useState();

  const { setSelectedScreen, isLoading, setIsLoading } = useContext(GlobalContext);

  const formatActivityFactor = (activityFactor) => {
    const formattedNames = {
      SEDENTARY: "Sedentário",
      LIGHTLY_ACTIVE: "Pouco ativo",
      MODERATELY_ACTIVE: "Moderadamente ativo",
      VERY_ACTIVE: "Muito ativo",
      EXTRA_ACTIVE: "Super ativo",
    }

    return formattedNames[activityFactor];
  }

  const fetchPatient = useCallback(async (id) => {
    if (!isLoading) {
      setIsLoading(true);
    }

    const user = await userService.getUserById(id);
    setPatient(user);

    const activeMealPlan = user?.meal_plans?.find((mealPlan) => mealPlan.status === "ACTIVE");
    setMealPlan(activeMealPlan);
  }, []);

  const fetchData = async () => {
    setIsLoading(true);

    const userFound = await loginService.getCurrentUser();
    setCurrentUser(userFound);

    setIsLoading(true);
    fetchPatient(patientId);

    setIsLoading(false);
  }

  useEffect(() => {
    document.title = "Dashboard";
    setSelectedScreen("patient");

    async function fetchCurrentUser() {
      const userFound = await loginService.getCurrentUser();
      setCurrentUser(userFound);
    }

    fetchData();
  }, []);

  const options = [
    {
      title: "Plano alimentar",
      description: "Gerenciar o plano alimentar do paciente",
      buttonTitle: "Visualizar",
      onClick: () => {
        window.location.href = `/patients/${patientId}/meal-plan`;
      },
    },
    {
      title: "Registro de Adesão",
      description: "Visualizar refeições registradas pelo paciente",
      buttonTitle: "Visualizar",
      onClick: () => {
        window.location.href = `/patients/${patientId}/progress`;
      },
    },
    {
      title: "Medidas Antropométricas",
      description: "Visualizar medidas antropométricas do paciente",
      buttonTitle: "Visualizar",
      onClick: () => {
        window.location.href = `/patients/${patientId}/anthropometry`;
      },
    },
    {
      title: "Consulta",
      description: "Agendamento de novas consultas com paciente",
      buttonTitle: "Marcar consulta",
      onClick: () => {
        window.location.href = `/appointments`;
      },
    }
  ]

  return (
    <div className="patient-screen">
      <div className="patient__content__header">
        <a href="/patients" className="patient__content__header__previous">
          Pacientes
        </a>
        <div className="patient__content__header__divisor">
          <img src={ChevronRightIcon} alt="Chevron Right Icon" />
        </div>
        <div className="patient__content__header__name">
          <p>{patient?.name}</p>
        </div>
      </div>
      {patient && (
        <div className="patient-screen__content">
          <div className="patient-screen__details">
            <div className="patient-screen__details__name">
              <div className="patient-screen__details__name__avatar">
                <img src={patient?.photo_url ?? DefaultUserIcon} alt="User Icon" />
              </div>
              <h2>{patient?.name}</h2>
            </div>
            <div className="patient-screen__details__personal">
              <div className="patient-screen__details__personal__title">
                <h3>Informações pessoais</h3>
              </div>
              <div className="patient-screen__details__personal__forms">
                <TextInput
                  label="Data de nascimento"
                  placeholder="Data de nascimento"
                  value={patient?.birth_date}
                />
                <div className="patient-screen__details__personal__forms__measurements">
                  <TextInput
                    label="Peso"
                    placeholder="Peso"
                    value={`${patient?.body_measurement?.weight ?? ''}`}
                  />
                  <TextInput
                    label="Altura"
                    placeholder="Altura"
                    value={`${patient?.body_measurement?.height || ''}`}
                  />
                </div>
                <DefaultDropdown
                  label="Nível de atividade física"
                  value={formatActivityFactor(patient?.physical_activity_factor)}
                  placeholder="Nível de atividade física"
                  options={[
                    { value: "SEDENTARY", label: "Sedentário" },
                    { value: "LIGHTLY_ACTIVE", label: "Pouco ativo" },
                    { value: "MODERATELY_ACTIVE", label: "Moderadamente ativo" },
                    { value: "VERY_ACTIVE", label: "Muito ativo" },
                    { value: "EXTRA_ACTIVE", label: "Super ativo" },
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="patient-screen__options">
            {options?.map((option) => (
              <div className="patient-screen__option" key={option.title}>
                <div className="patient-screen__option__text">
                  <div className="patient-screen__option__title">
                    <h3>{option.title}</h3>
                  </div>
                  <div className="patient-screen__option__description">
                    <p>{option.description}</p>
                  </div>
                </div>
                <div className="patient-screen__option__button">
                  <a href="javascript:;" onClick={option.onClick}>
                    {option.buttonTitle}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
};

export default Patient;

Patient.propTypes = {};
